import {groupByUniqueKey, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {useMemo} from 'react';
import * as uuid from 'uuid';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, RED} from 'ui-library/Colors';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import ReorderableSimpleTableRow, {
  ReorderableSimpleTableRowProps,
} from 'ui-library/reorderable/ReorderableSimpleTableRow';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {isPlaceholderTaskSchema, AvailableTaskSchema} from './FlowchartConfigConstants';
import SelectTaskSchemaDropdown from './SelectTaskSchemaDropdown';

export interface AvailableTaskSchemasPaneProps {
  isEditing: boolean;
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  availableTaskSchemas: Array<AvailableTaskSchema>;
  onChange: (availableTaskSchemas: Array<AvailableTaskSchema>) => void;
}

export default function AvailableTaskSchemasPane(props: AvailableTaskSchemasPaneProps): JSX.Element {
  const [nodeCompatibleTaskSchemas, taskSchemasById] = useMemo(() => {
    const filteredTaskSchemas = props.allTaskSchemas.filter(
      ({executionType, archiveRecord}) =>
        executionType !== ProcessExecutionType.FLOWCHART && archiveRecord == null
    );
    const filteredTaskSchemasById = groupByUniqueKey(filteredTaskSchemas, (item) => item.id);
    return [filteredTaskSchemas, filteredTaskSchemasById];
  }, [props.allTaskSchemas]);

  const usedTaskSchemaIds = new Set(
    props.availableTaskSchemas
      .filter((taskSchema) => !isPlaceholderTaskSchema(taskSchema))
      .map(({id}: ProcessTaskSchemaGraphQL) => id)
  );
  const possibleTaskSchemas = nodeCompatibleTaskSchemas.filter(({id}) => !usedTaskSchemaIds.has(id));

  const getReorderableRowItem = (
    rowItem: AvailableTaskSchema,
    idx: number
  ): ItemData<ReorderableSimpleTableRowProps> => {
    const rowId = isPlaceholderTaskSchema(rowItem) ? rowItem.placeholderClientId : rowItem.id;
    return {
      id: rowId,
      itemClass: ReorderableSimpleTableRow,
      itemProps: {
        isDraggingDisabled: !props.isEditing,
        rowContainerClassName: css(styles.listItemContainer),
        dragHandleClassName: css(styles.dragHandle),
        row: {
          id: rowId,
          cells: [
            {
              key: `${rowId}-selectTaskSchema`,
              contents: (
                <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                  <SelectTaskSchemaDropdown
                    taskSchema={isPlaceholderTaskSchema(rowItem) ? null : rowItem}
                    possibleTaskSchemas={
                      // If a task schema is selected, it should appear as an option in the dropdown even though
                      // it is "used".
                      isPlaceholderTaskSchema(rowItem)
                        ? possibleTaskSchemas
                        : [rowItem, ...possibleTaskSchemas]
                    }
                    onSelectTaskSchemaId={(taskSchemaId) => {
                      if (taskSchemaId === rowId) {
                        return;
                      }
                      if (usedTaskSchemaIds.has(taskSchemaId)) {
                        notifications.error(
                          'Cannot select a task schema thats already included in the flowchart configuration'
                        );
                        return;
                      }
                      props.onChange(
                        produce(props.availableTaskSchemas, (draftAvailableTaskSchemas) => {
                          draftAvailableTaskSchemas[idx] = taskSchemasById[taskSchemaId];
                          return draftAvailableTaskSchemas;
                        })
                      );
                    }}
                    isLink={true}
                    disabled={!props.isEditing}
                    dataTestAttrs={{
                      component: 'AvailableTaskSchemasPane',
                      element: 'selectTaskSchemaDropdown',
                      key: rowId,
                    }}
                  />
                </div>
              ),
            },
            ...(props.isEditing
              ? [
                  {
                    key: `${rowId}-delete`,
                    cellClassName: 'simpleDivTable-actionGroupContainer',
                    contents: (
                      <div className={css(styles.buttonContainer)}>
                        <TooltipWrapper tooltipTitle="Remove from flowchart config">
                          <Button
                            buttonStyle={ButtonStyle.LINK}
                            className={classNames(css(styles.singleButton), css(styles.deleteButton))}
                            icon="delete"
                            onClick={() => {
                              props.onChange(
                                produce(props.availableTaskSchemas, (draftAvailableTaskSchemas) => {
                                  draftAvailableTaskSchemas.splice(idx, 1);
                                  return draftAvailableTaskSchemas;
                                })
                              );
                            }}
                            dataTestAttrs={{
                              component: 'AvailableTaskSchemasPane',
                              element: 'removeTaskSchemaButton',
                              key: rowId,
                            }}
                          />
                        </TooltipWrapper>
                      </div>
                    ),
                  },
                ]
              : []),
          ],
        },
      },
    };
  };

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AvailableTaskSchemasPane', element: 'root'})}
    >
      {props.isEditing && (
        <Button
          className={classNames(css(styles.singleButton), css(styles.addButton))}
          textClassName={css(styles.addButtonText)}
          buttonStyle={ButtonStyle.LINK}
          onClick={() => {
            props.onChange([...props.availableTaskSchemas, {placeholderClientId: uuid.v4()}]);
          }}
          icon="compose"
          text="Add task schema"
          dataTestAttrs={{
            component: 'AvailableTaskSchemasPane',
            element: 'addTaskSchemaButton',
          }}
        />
      )}
      {props.availableTaskSchemas.length === 0 ? (
        <div>No tasks schemas added to flowchart configuration.</div>
      ) : (
        <div className={css(styles.listWrapper)}>
          <ReorderableList
            itemDatas={props.availableTaskSchemas.map((taskSchema, idx) =>
              getReorderableRowItem(taskSchema, idx)
            )}
            onMoveItem={(ids) => {
              props.onChange(ids.map((rowId) => taskSchemasById[rowId] ?? {placeholderClientId: rowId}));
            }}
            onMoveItemEnd={noop}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    margin: '16px 0',
  },
  listWrapper: {
    borderLeft: `1px solid ${GRAY_LIGHT}`,
    borderRight: `1px solid ${GRAY_LIGHT}`,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mappingColumnHeader: {paddingLeft: '50px'},
  dropdownContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  dragHandle: {
    marginRight: 0,
    width: '16px',
  },
  buttonContainer: {
    marginLeft: '15px',
  },
  singleButton: {
    padding: '0px',
    boxShadow: 'none',
  },
  deleteButton: {
    paddingLeft: '8px',
    paddingRight: '8px',
    color: GRAY,
    ':hover': {
      color: RED,
    },
  },
  addButton: {
    marginBottom: '16px',
    marginTop: '-7px',
  },
  addButtonText: {
    marginLeft: '4px',
  },
  listItemContainer: {
    marginLeft: '-16px',
  },
});
